import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Box, Input, Checkbox, Select } from '@robocorp/components';
import { useTranslation } from 'react-i18next';

import { z } from 'zod';
import type { ApiParam } from '../ApiEndpoints';

type Props = {
  params: ApiParam[];
  onChange: (output: string) => void;
};

export const Params: FC<Props> = ({ params, onChange }) => {
  const { t } = useTranslation();
  const [parameters, setParameters] = useState<
    Record<
      string,
      {
        config: ApiParam;
        error: string | undefined;
        value: boolean | string | number | null | undefined;
      }
    >
  >({});

  useEffect(() => {
    if (params) {
      setParameters(
        params.reduce(
          (acc, param) => ({
            ...acc,
            [param.name]: { config: param, error: null, value: param.default },
          }),
          {},
        ),
      );
    } else {
      setParameters({});
    }
  }, [params]);

  useEffect(() => {
    if (params) {
      const result = Object.values(parameters)
        // exclude from the qs if the parameter is undefined
        .filter((param) => param.value !== undefined)
        .map(
          (param) =>
            `${param.config.name}=${
              // set as the default value if we have validation error
              param.error ? param.config.default : param.value
            }`,
        )
        .join('&');
      onChange(`?${result}`);
    }
  }, [parameters, onChange]);

  const updateBoolean = useCallback(
    (name: string) => () => {
      const result = { ...parameters };

      result[name].value = !result[name].value;

      setParameters(result);
    },
    [parameters],
  );

  const updateString = useCallback(
    (name: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const result = { ...parameters };
      const {
        schema = z
          .string()
          .optional()
          // transform empty string as undefined so that the qs doesn't contain empty search param
          .transform((value) => (value?.length ? value : undefined)),
      } = parameters[name].config;

      let { value } = e.target;

      if (schema) {
        const parsed = schema.safeParse(value);

        if (!parsed.success) {
          result[name].error = parsed.error.issues.find(Boolean)?.message;
        } else {
          value = parsed.data;
          result[name].error = undefined;
        }
      }

      result[name].value = value;

      setParameters(result);
    },
    [parameters],
  );

  const updateNumber = useCallback(
    (name: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const result = { ...parameters };
      const { schema = z.number().min(1) } = parameters[name].config;

      const value = parseInt(e.target.value, 10) || undefined;
      const parsed = schema.safeParse(value);

      if (!parsed.success) {
        result[name].error = parsed.error.issues.find(Boolean)?.message;
      } else {
        result[name].error = undefined;
      }

      result[name].value = value;
      setParameters(result);
    },
    [parameters],
  );

  const updateEnum = useCallback(
    (name: string) => (value: string) => {
      const result = { ...parameters };
      const { schema } = parameters[name].config;

      if (schema) {
        const parsed = schema.safeParse(value);

        if (!parsed.success) {
          result[name].error = parsed.error.issues.find(Boolean)?.message;
        } else {
          result[name].error = undefined;
        }
      }

      result[name].value = value;
      setParameters(result);
    },
    [parameters],
  );

  if (!params) {
    return null;
  }

  return (
    <>
      <Box mb={16}>{t('APIHelper.additionalParameters')}</Box>
      {Object.values(parameters).map((param) => {
        const { config } = param;
        if (config.type === 'boolean') {
          return (
            <Box mb={10}>
              <Checkbox
                key={config.name}
                onChange={updateBoolean(config.name)}
                checked={param.value as boolean}
                label={t(config.label)}
              />
            </Box>
          );
        }

        if (config.type === 'string') {
          return (
            <Box mb={20}>
              <Input
                key={config.name}
                value={param.value as string}
                onChange={updateString(config.name)}
                label={t(config.label)}
                error={parameters[config.name].error}
              />
            </Box>
          );
        }

        if (config.type === 'number') {
          return (
            <Box mb={20}>
              <Input
                key={config.name}
                value={param.value as string | number}
                type="number"
                onChange={updateNumber(config.name)}
                label={t(config.label)}
                error={parameters[config.name].error}
              />
            </Box>
          );
        }

        if (config.type === 'enum') {
          return (
            <Box mb={20}>
              <Select
                items={config.options?.map((value) => ({ label: value, value })) ?? []}
                label={t(config.label)}
                onChange={updateEnum(config.name)}
                value={param.value as string}
                error={parameters[config.name].error}
              />
            </Box>
          );
        }

        return null;
      })}
    </>
  );
};
